import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Home from "./pages/Home";
import Results from "./pages/Results";
import Detail from "./pages/Detail";
import Saved from "./pages/Saved";
import { FlyingImageProvider, useFlyingImage } from "./transitions/FlyingImage";
import { TopPanel, SwappableStrip, type TabId } from "./components/TopPanel";
import { AuthProvider, LoginPage, useAuth } from "./auth";
import "./tab-slide.css";

function Shell() {
  const { user } = useAuth();
  if (!user) return <LoginPage />;

  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isDetail = pathname.startsWith("/asset/");
  const isSaved = pathname === "/saved";
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("esplora");
  const [stripExiting, setStripExiting] = useState(false);
  const activeRef = isDetail ? pathname.split("/asset/")[1] : undefined;
  const isResultsOrSaved = pathname === "/results" || pathname === "/saved";
  const headerRef = useRef<HTMLDivElement>(null);
  const { setTargetCenter } = useFlyingImage();

  const handleDetailExit = (to: string) => {
    setStripExiting(true);
    setTimeout(() => {
      navigate(to);
      setStripExiting(false);
    }, 1400);
  };

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty(
        "--top-panel-h",
        `${el.offsetHeight}px`,
      );
      // When on the detail page, store the exact field center for the next trigger
      if (isDetail) {
        const fieldW = window.innerWidth - 296;
        const fieldH = window.innerHeight - el.offsetHeight;
        setTargetCenter(fieldW / 2, el.offsetHeight + fieldH / 2);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isHome, isDetail, setTargetCenter]);

  return (
    <div className={isHome ? undefined : "shell-clip"}>
      {!isHome && (
        <div ref={headerRef} className="panel-shell">
          <TopPanel
            mode={isDetail ? "detail" : "results"}
            onExit={isDetail ? handleDetailExit : undefined}
          />
          {!isSaved && (
            <SwappableStrip
              mode={isDetail ? "detail" : "results"}
              tab={tab}
              onTabChange={setTab}
              activeRef={activeRef}
              exiting={stripExiting}
            />
          )}
        </div>
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/results" element={null} />
        <Route path="/saved" element={null} />
        <Route
          path="/asset/:ref"
          element={
            <Detail
              tab={tab}
              exiting={stripExiting}
              onExit={handleDetailExit}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {isResultsOrSaved && (
        <div className="tab-pages" data-tab={isSaved ? "saved" : "results"}>
          <div className="tab-page tab-page--results">
            <Results />
          </div>
          <div className="tab-page tab-page--saved">
            <Saved />
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FlyingImageProvider>
        <HashRouter>
          <Shell />
        </HashRouter>
      </FlyingImageProvider>
    </AuthProvider>
  );
}
